import React, { useRef, useState, useCallback, useEffect } from 'react';

const TIMEOUT_MS = Number(process.env.REACT_APP_IFRAME_TIMEOUT) || 10000;

interface EmbeddedWebpageProps {
    src: string;
    title: string;
    queryParams?: Record<string, string>;
    timeoutMs?: number;
    onLoadError?: (reason: string) => void;
    fallbackMessage?: string;
    retryLabel?: string;
}

type LoadState = 'loading' | 'loaded' | 'error';

const EmbeddedWebpage: React.FC<EmbeddedWebpageProps> = ({
    src,
    title,
    queryParams = {},
    timeoutMs = TIMEOUT_MS,
    onLoadError,
    fallbackMessage,
    retryLabel,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [loadState, setLoadState] = useState<LoadState>('loading');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startTimeRef = useRef<number>(0);
    const srcSetRef = useRef(false);

    const effectiveSrc = appendQueryParams(src, queryParams);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const handleLoadFailure = useCallback((reason: string) => {
        clearTimer();
        const elapsed = Date.now() - startTimeRef.current;
        try {
            const urlHost = new URL(effectiveSrc).host;
            console.warn(
                `[EmbeddedWebpage] iframe load failed: reason=${reason}, host=${urlHost}, elapsed=${elapsed}ms`
            );
        } catch {
            console.warn(
                `[EmbeddedWebpage] iframe load failed: reason=${reason}, elapsed=${elapsed}ms`
            );
        }
        setLoadState('error');
        onLoadError?.(reason);
    }, [clearTimer, effectiveSrc, onLoadError]);

    const handleLoadSuccess = useCallback(() => {
        // Ignore onLoad from about:blank (fires before we set the real src)
        if (!srcSetRef.current) {
            return;
        }

        // Ignore late onLoad if we already timed out — only Retry should clear error state
        if (timerRef.current === null) {
            return;
        }

        clearTimer();
        setLoadState('loaded');
    }, [clearTimer]);

    const loadIframe = useCallback(() => {
        setLoadState('loading');
        srcSetRef.current = false;
        startTimeRef.current = Date.now();

        clearTimer();
        timerRef.current = setTimeout(() => {
            handleLoadFailure('timeout');
        }, timeoutMs);

        // Verify server reachability before setting iframe src.
        // mode: 'no-cors' gives an opaque response — we can't read the HTTP
        // status, but the promise rejects for network errors (DNS failure,
        // connection refused). This avoids the browser rendering its own
        // error page in the iframe which fires onLoad misleadingly.
        fetch(effectiveSrc, { mode: 'no-cors' })
            .then(() => {
                if (iframeRef.current && timerRef.current !== null) {
                    iframeRef.current.src = effectiveSrc;
                    srcSetRef.current = true;
                }
            })
            .catch(() => {
                if (timerRef.current !== null) {
                    handleLoadFailure('network');
                }
            });
    }, [effectiveSrc, timeoutMs, clearTimer, handleLoadFailure]);

    useEffect(() => {
        loadIframe();

        return clearTimer;
    }, [loadIframe, clearTimer]);

    const handleRetry = useCallback(() => {
        loadIframe();
    }, [loadIframe]);

    // Hide iframe until fully loaded to prevent the raw background flash (BAS-13585)
    const iframeVisible = loadState === 'loaded';

    return (
        <>
            <iframe
                ref={iframeRef}
                title={title}
                style={{
                    border: 'none',
                    display: iframeVisible ? 'block' : 'none',
                }}
                onLoad={handleLoadSuccess}
                onError={() => handleLoadFailure('error')}
            />
            {loadState === 'loading' && (
                <div className="iframe-loading" data-test="iframe-loading">
                    <div className="loader"><div /><div /><div /><div /><div /><div /><div /><div /></div>
                </div>
            )}
            {loadState === 'error' && (
                <div className="iframe-fallback" data-test="iframe-fallback">
                    <div className="iframe-fallback__content">
                        <p className="iframe-fallback__message">
                            {fallbackMessage ?? 'No contact with server'}
                        </p>
                        <button
                            className="iframe-fallback__retry"
                            onClick={handleRetry}
                            data-test="iframe-fallback-retry"
                            type="button"
                        >
                            {retryLabel ?? 'Retry'}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

const appendQueryParams = (src: string, params: {}) => {
    const url = new URL(src);
    Object.keys(params).forEach(key => {
        url.searchParams.append(key, params[key]);
    });

    return url.toString();
};

export default EmbeddedWebpage;
